/**
 * C05 Day-1 Hook — 7-day login calendar UI.
 *
 * Renders a 7-tile grid showing each day's reward + claim state. Tiles fall
 * into one of four states:
 *
 *   - 'claimed'   (✓)  — already claimed, dim + checkmark
 *   - 'pending'   (🎁) — claimable RIGHT NOW, brightly highlighted + clickable
 *   - 'locked'    (🔒) — future day, dim + lock
 *
 * Day-7 always shows the ★ marker for the milestone reward.
 *
 * Wiring: home banner calls `renderDayOneCalendar(container, p, { onClaim, onClose })`.
 * Calendar invokes `onClaim(day)` after a successful claim so the banner can
 * refresh / surface a confetti animation. Calendar never mutates progress
 * directly — it calls `claimDay` from dayone-hook and forwards the result.
 */

import type { PlayerProgress } from '../shared/types';
import { DAY_REWARDS, claimDay, snapshotHook } from './dayone-hook';

export interface DayOneCalendarActions {
  /** Fired after a successful claim with the awarded day number. */
  onClaim?: (day: number) => void;
  /** Fired when the kid taps the close button or backdrop. */
  onClose?: () => void;
  /** Override Date.now() for deterministic tests. */
  nowMs?: number;
}

/**
 * Mount the calendar overlay into `container`. Returns an unmount function
 * that removes the overlay (caller is responsible for cleaning up on route
 * change).
 *
 * Layout:
 *   .dayone-calendar
 *     .dayone-calendar-header  (title + close)
 *     .dayone-calendar-grid    (7 tiles)
 *     .dayone-calendar-footer  (progress text)
 */
export function renderDayOneCalendar(
  container: HTMLElement,
  progress: PlayerProgress,
  actions: DayOneCalendarActions = {},
): () => void {
  const nowMs = actions.nowMs ?? Date.now();
  const root = document.createElement('div');
  root.className = 'dayone-calendar';
  root.setAttribute('data-testid', 'dayone-calendar');

  renderInto(root, progress, nowMs, actions);
  container.appendChild(root);

  return () => {
    root.remove();
  };
}

/** Re-render `root` based on current `progress` snapshot. */
function renderInto(
  root: HTMLElement,
  progress: PlayerProgress,
  nowMs: number,
  actions: DayOneCalendarActions,
): void {
  const snap = snapshotHook(progress, nowMs);

  const tilesHtml = DAY_REWARDS.map((r) => {
    const claimed = snap.claimedDays.includes(r.day);
    const pending = snap.pendingDays.includes(r.day);
    const locked = !claimed && !pending;
    const state = claimed ? 'claimed' : pending ? 'pending' : 'locked';
    const icon = claimed ? '✓' : pending ? '🎁' : '🔒';
    const star = r.star ? '★' : '';
    return `
      <button class="dayone-tile dayone-tile--${state}${r.star ? ' dayone-tile--star' : ''}"
              data-day="${r.day}"
              data-state="${state}"
              ${locked ? 'disabled' : ''}
              aria-label="第 ${r.day} 天獎勵 ${r.freeXP} 修煉值 (${state})">
        <div class="dayone-tile__day">D${r.day}${star}</div>
        <div class="dayone-tile__icon">${icon}</div>
        <div class="dayone-tile__xp">+${r.freeXP}</div>
      </button>
    `;
  }).join('');

  // QA #263 fix #3 (calendar footer): when MORE than one day is pending (kid
  // missed N days then returned), surface the total count instead of only the
  // last pending day. The old copy "今天可以領 D5!" hid the fact that D2/D3/D4
  // were also claimable. New copy keeps the single-day phrasing for the common
  // case but switches to a count summary when multiple days are claimable.
  const pendingFooter = snap.pendingDays.length > 1
    ? ` — 今天可以領 ${snap.pendingDays.length} 個獎勵!`
    : snap.pendingDays.length === 1
      ? ` — 今天可以領 D${snap.pendingDays[0]}!`
      : '';
  root.innerHTML = `
    <div class="dayone-calendar__header">
      <h2>七日簽到禮包</h2>
      <button class="dayone-calendar__close" aria-label="關閉">✕</button>
    </div>
    <div class="dayone-calendar__grid">${tilesHtml}</div>
    <div class="dayone-calendar__footer">
      已領取 <strong>${snap.claimedDays.length}</strong> / 7 天${pendingFooter}
    </div>
  `;

  // Wire close
  const closeBtn = root.querySelector('.dayone-calendar__close') as HTMLButtonElement | null;
  if (closeBtn) {
    closeBtn.addEventListener('click', () => actions.onClose?.());
  }

  // Wire tile clicks (only pending tiles claim)
  for (const btn of Array.from(root.querySelectorAll<HTMLButtonElement>('.dayone-tile--pending'))) {
    const day = Number(btn.dataset.day);
    btn.addEventListener('click', () => {
      const reward = claimDay(progress, day, nowMs);
      if (reward) {
        actions.onClaim?.(reward.day);
        // Re-render to reflect new claimed state.
        renderInto(root, progress, nowMs, actions);
      }
    });
  }
}
